import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Progress,
  Radio,
  Checkbox,
  Input,
  Typography,
  Space,
  Alert,
  Result,
  Spin,
  Select,
  Image,
  Tag,
} from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import Layout from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getStaticImageUrl } from '../services/api';
import { TestAnswer } from '../types';
import { useTestSession } from '../hooks/useTestSession';
import { formatTime } from '../utils/formatTime';

const { Title, Paragraph } = Typography;

/** Подпись к фото: из переданной подписи или из URL (имя файла без расширения, например HAB-110). */
function getExplanationImageCaption(captionFromData: string | undefined, url: string, fallbackIndex: number): string {
  if (captionFromData && captionFromData.trim()) return captionFromData.trim();
  if (!url || typeof url !== 'string') return `Пояснение ${fallbackIndex + 1}`;
  const s = url.trim();
  const lastSlash = s.lastIndexOf('/');
  const lastPart = lastSlash >= 0 ? s.slice(lastSlash + 1) : s;
  const dot = lastPart.lastIndexOf('.');
  const name = dot >= 0 ? lastPart.slice(0, dot) : lastPart;
  return name || `Пояснение ${fallbackIndex + 1}`;
}

const TestPage: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { testData, currentQuestionIndex, answers, answerResults, correctAnswerIds, correctMatching,
    timeLeft, loading, submitting, completed, result, requestError, busyLabel, inputLocked,
    upsertAnswer, handleNext, handlePrevious, retryRequest } = useTestSession(testId);
  const [questionImageError, setQuestionImageError] = useState<Record<number, boolean>>({});
  useEffect(() => setQuestionImageError({}), [testId]);

  const toNumericIds = useCallback((ids?: Array<number | string | null | undefined>): number[] => {
    return (ids || [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }, []);

  const getAnswerVisualState = useCallback(
    (questionId: number, answerId: number, currentAnswer?: TestAnswer) => {
      const selectedIds = toNumericIds(currentAnswer?.selected_answer_ids);
      const correctIds = toNumericIds(correctAnswerIds[questionId] as Array<number | string> | undefined);
      const isSelected = selectedIds.includes(Number(answerId));
      const isCorrectById = correctIds.includes(Number(answerId));
      const submitted = answerResults[questionId] !== undefined;
      const questionIsCorrect = answerResults[questionId] === true;

      const selectedCorrect = submitted && isSelected && isCorrectById;
      const selectedWrong = submitted && isSelected && !isCorrectById;
      const showCorrect = submitted && !questionIsCorrect && !isSelected && isCorrectById;

      return {
        submitted,
        isSelected,
        selectedCorrect,
        selectedWrong,
        showCorrect,
      };
    },
    [answerResults, correctAnswerIds, toNumericIds]
  );

  const handleAnswerChange = (questionId: number, value: any, type: string) => {
    if (inputLocked) return;
    const answer: TestAnswer = { question_id: questionId };
    if (type === 'single_choice') answer.selected_answer_ids = toNumericIds([value]);
    else if (type === 'multiple_choice') answer.selected_answer_ids = toNumericIds(value);
    else if (type === 'open_answer') answer.answer_text = value;
    else if (type === 'matching') answer.matching_data = value;
    upsertAnswer(questionId, answer);
  };

  // Форматирование времени для таймера (MM:SS формат)
  const formatTimerTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" /><p role="status">Загружаем тест…</p>
        </div>
      </Layout>
    );
  }

  if (completed && result) {
    const wrongAnswers = (result.answers || []).filter((a: any) => a.is_correct === false);
    const questionsWithExplanation = testData
      ? wrongAnswers
          .map((a: any) => {
            const q = testData.questions.find((qu: any) => qu.id === a.question_id);
            return q && (q.explanation || (q.explanation_image_urls?.length ?? 0) > 0) ? { answer: a, question: q } : null;
          })
          .filter(Boolean) as { answer: any; question: any }[]
      : [];

    return (
      <Layout>
        <Result
          status={result.is_passed ? 'success' : 'error'}
          title={result.is_passed ? t('test.passed') : t('test.failed')}
          subTitle={
            <>
              <p>Ваш результат: {result.score?.toFixed(1)}%</p>
              <p>Время прохождения: {formatTime(result.time_spent_seconds || 0)}</p>
            </>
          }
          extra={[
            <Button key="results" type="primary" onClick={() => navigate('/results')}>
              Посмотреть результаты
            </Button>,
            <Button key="tests" onClick={() => navigate('/')}>
              Личный кабинет
            </Button>,
          ]}
        />
        {questionsWithExplanation.length > 0 && (
          <div className="explanations-block">
            <Title level={4} style={{ marginBottom: 16 }}>
              Пояснения к неверным ответам
            </Title>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {questionsWithExplanation.map(({ answer, question }) => (
                <Card
                  key={answer.question_id}
                  size="small"
                  style={{
                    background: '#fafafa',
                    border: '1px solid #e8e8e8',
                    borderRadius: '12px',
                  }}
                  bodyStyle={{ padding: '20px' }}
                >
                  <Paragraph strong style={{ marginBottom: 8 }}>
                    {question.text}
                  </Paragraph>
                  <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
                    Тип: {question.type === 'single_choice' ? 'Один ответ' : question.type === 'multiple_choice' ? 'Несколько ответов' : question.type}
                  </Paragraph>
                  {question.answers?.length > 0 && (
                    <div style={{ marginBottom: 12, fontSize: 13 }}>
                      <span style={{ color: '#8c8c8c' }}>Варианты ответов:</span>
                      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                        {question.answers.map((ans: any) => (
                          <li key={ans.id}>
                            {ans.text}
                            {correctAnswerIds[question.id]?.includes(ans.id) && (
                              <Tag color="success" style={{ marginLeft: 8 }}>✓ Правильный</Tag>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 8, fontWeight: 600 }}>
                    Пояснение
                  </div>
                  {question.explanation && (
                    <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: question.explanation_image_urls?.length ? 12 : 0 }}>
                      {question.explanation}
                    </Paragraph>
                  )}
                  {question.explanation_image_urls?.map((url: string, idx: number) => {
                    const caption = getExplanationImageCaption(question.explanation_image_captions?.[idx], url, idx);
                    return (
                      <div key={idx} style={{ marginBottom: idx < (question.explanation_image_urls?.length ?? 0) - 1 ? 12 : 0 }}>
                        <Image
                          src={getStaticImageUrl(url)}
                          alt={caption}
                          style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 8 }}
                          preview={{ mask: 'Увеличить' }}
                        />
                        <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: '#262626' }}>
                          {caption}
                        </div>
                      </div>
                    );
                  })}
                </Card>
              ))}
            </Space>
          </div>
        )}
      </Layout>
    );
  }

  if (!testData) {
    return <Layout><Result status="warning" title="Не удалось открыть тест"
      subTitle={requestError?.message}
      extra={<Space>
        {requestError?.action !== 'closed' && <Button type="primary" onClick={() => void retryRequest()}>Повторить загрузку</Button>}
        <Button onClick={() => navigate('/results')}>Посмотреть результаты</Button>
        <Button onClick={() => navigate('/')}>Личный кабинет</Button>
      </Space>} /></Layout>;
  }

  const currentQuestion = testData.questions[currentQuestionIndex];
  
  // Проверка на существование текущего вопроса
  if (!currentQuestion) {
    return (
      <Layout>
        <Card>
          <Result
            status="error"
            title={t('common.error')}
            subTitle={t('test.questionNotFound') || 'Вопрос не найден'}
          />
        </Card>
      </Layout>
    );
  }

  const progress = Number((((currentQuestionIndex + 1) / testData.questions.length) * 100).toFixed(1)); // Округление до 1 знака после запятой
  const currentAnswer = answers[currentQuestion.id];

  return (
    <Layout>
      <Card className="test-page-card responsive-container">
        {requestError && <Alert showIcon type="error" message={requestError.message} style={{ marginBottom: 16 }}
          action={requestError.action === 'closed'
            ? <Button onClick={() => navigate('/')}>Личный кабинет</Button>
            : <Button onClick={() => void retryRequest()} loading={submitting}>
              {requestError.action === 'sync' ? 'Проверить сохранение' : requestError.action === 'complete' ? 'Повторить завершение' : 'Повторить отправку'}
            </Button>} />}
        {submitting && <p role="status">{busyLabel}</p>}
        {timeLeft === 0 && !requestError && <Alert type="warning" showIcon style={{ marginBottom: 16 }}
          message="Время прохождения истекло. Завершаем тест по сохранённым ответам." />}
        <div style={{ marginBottom: 24 }}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Title level={4}>{testData.test_title}</Title>
            <Typography.Text strong style={{ fontSize: 18 }}>
              {t('test.timeLeft')}: {formatTimerTime(timeLeft)}
            </Typography.Text>
          </Space>
          <Progress 
            percent={progress} 
            format={(percent) => `${percent?.toFixed(1)}%`}
            style={{ marginTop: 16 }} 
          />
          <Paragraph style={{ marginTop: 8 }}>
            {t('test.question')} {currentQuestionIndex + 1} {t('test.of')} {testData.questions.length}
          </Paragraph>
        </div>

        <Card className="test-question-inner-card" bodyStyle={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24, width: '100%', position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', position: 'relative' }}>
              <Title level={4} style={{ margin: 0, textAlign: 'center' }}>{currentQuestion.text}</Title>
            </div>
            {currentQuestion.type === 'multiple_choice' && (
              <Tag color="blue" style={{ marginTop: 8 }}>
                {t('test.multipleChoiceHint')}
              </Tag>
            )}
            {answerResults[currentQuestion.id] !== undefined && (
              <div className="test-answer-badge" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8,
                padding: '8px 16px',
                borderRadius: '20px',
                backgroundColor: answerResults[currentQuestion.id] ? '#f6ffed' : '#fff1f0',
                border: `1px solid ${answerResults[currentQuestion.id] ? '#b7eb8f' : '#ffccc7'}`
              }}>
                {answerResults[currentQuestion.id] ? (
                  <>
                    <CheckOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                    <span style={{ color: '#52c41a', fontWeight: 600 }}>Правильно</span>
                  </>
                ) : (
                  <>
                    <CloseOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
                    <span style={{ color: '#ff4d4f', fontWeight: 600 }}>Неправильно</span>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Отображение изображения для всех типов вопросов, если оно есть и не было ошибки загрузки */}
          {currentQuestion.image_url && !questionImageError[currentQuestion.id] && (() => {
            const resolvedUrl = getStaticImageUrl(currentQuestion.image_url);
            return (
            <div className="test-question-image-wrap">
              <Image
                src={resolvedUrl}
                alt={t('test.part') || 'Изображение детали'}
                className="test-question-main-image"
                preview={{
                  mask: t('test.zoom') || 'Увеличить'
                }}
                onError={() => setQuestionImageError(prev => ({ ...prev, [currentQuestion.id]: true }))}
              />
            </div>
            );
          })()}

          {currentQuestion.type === 'image_identification' && (
            <div style={{ marginTop: 16 }}>
              <div style={{ marginBottom: 16 }}>
                <Paragraph strong style={{ marginBottom: 8 }}>
                  {t('test.imageIdentification')}
                </Paragraph>
                <>
                    <Input
                      placeholder={t('test.enterPartName')}
                      value={currentAnswer?.answer_text?.split('|')[0] || ''}
                      onChange={(e) => {
                        const parts = (currentAnswer?.answer_text || '').split('|');
                        const newText = `${e.target.value}|${parts[1] || ''}`;
                        handleAnswerChange(currentQuestion.id, newText, 'open_answer');
                      }}
                      disabled={answerResults[currentQuestion.id] !== undefined || inputLocked}
                      style={{ marginBottom: 12 }}
                    />
                    <Input
                      placeholder={t('test.enterPartNumber')}
                      value={currentAnswer?.answer_text?.split('|')[1] || ''}
                      onChange={(e) => {
                        const parts = (currentAnswer?.answer_text || '').split('|');
                        const newText = `${parts[0] || ''}|${e.target.value}`;
                        handleAnswerChange(currentQuestion.id, newText, 'open_answer');
                      }}
                      disabled={answerResults[currentQuestion.id] !== undefined || inputLocked}
                    />
                  </>
              </div>
              {answerResults[currentQuestion.id] !== undefined && (
                <div style={{ 
                  marginTop: 16,
                  padding: '12px 16px',
                  borderRadius: '8px',
                  backgroundColor: answerResults[currentQuestion.id] ? '#f6ffed' : '#fff1f0',
                  border: `1px solid ${answerResults[currentQuestion.id] ? '#b7eb8f' : '#ffccc7'}`
                }}>
                  {answerResults[currentQuestion.id] ? (
                    <>
                      <CheckOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                      <span style={{ color: '#52c41a' }}>Правильный ответ</span>
                    </>
                  ) : (
                    <>
                      <CloseOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                      <span style={{ color: '#ff4d4f' }}>Неправильный ответ</span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {currentQuestion.type === 'single_choice' && (
            <div className="test-answer-options-wrap">
              <Radio.Group
                value={currentAnswer?.selected_answer_ids?.[0]}
                onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value, 'single_choice')}
                disabled={answerResults[currentQuestion.id] !== undefined || inputLocked}
              >
                {currentQuestion.answers.some(ans => ans.image_url) ? (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'flex-start',
                    gap: 16, 
                    flexWrap: 'wrap',
                    width: '100%',
                    marginTop: 16
                  }}>
                    {currentQuestion.answers.map((answer) => {
                    const { selectedCorrect, selectedWrong, showCorrect } =
                      getAnswerVisualState(currentQuestion.id, answer.id, currentAnswer);
                    
                    return (
                      <div
                        key={answer.id}
                        onClick={() => {
                          if (answerResults[currentQuestion.id] === undefined && !inputLocked) {
                            handleAnswerChange(currentQuestion.id, answer.id, 'single_choice');
                          }
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: answer.image_url ? 'column' : 'row',
                          alignItems: 'center',
                          justifyContent: answer.image_url ? 'flex-start' : 'flex-start',
                          gap: 12,
                          padding: answer.image_url ? '16px' : '12px 16px',
                          borderRadius: '8px',
                          border: selectedCorrect
                            ? '2px solid #52c41a'
                            : selectedWrong
                            ? '2px solid #ff4d4f'
                            : showCorrect
                            ? '2px solid #52c41a'
                            : '1px solid #d9d9d9',
                          backgroundColor: selectedCorrect
                            ? '#f6ffed'
                            : selectedWrong
                            ? '#fff1f0'
                            : showCorrect
                            ? '#f6ffed'
                            : '#ffffff',
                          transition: 'all 0.3s ease',
                          cursor: answerResults[currentQuestion.id] === undefined && !inputLocked ? 'pointer' : 'default',
                          userSelect: 'none',
                          width: answer.image_url ? '220px' : '100%',
                          minHeight: answer.image_url ? '280px' : '48px',
                          boxSizing: 'border-box'
                        }}
                        onMouseEnter={(e) => {
                          if (answerResults[currentQuestion.id] === undefined && !inputLocked) {
                            e.currentTarget.style.backgroundColor = '#f0f0f0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (answerResults[currentQuestion.id] === undefined && !inputLocked) {
                            e.currentTarget.style.backgroundColor = selectedCorrect
                              ? '#f6ffed'
                              : selectedWrong
                              ? '#fff1f0'
                              : showCorrect
                              ? '#f6ffed'
                              : '#ffffff';
                          }
                        }}
                      >
                        {answer.image_url ? (
                          <>
                            {/* Изображение сверху */}
                            <div style={{ position: 'relative', flexShrink: 0 }}>
                              <Image
                                src={getStaticImageUrl(answer.image_url)}
                                alt="Answer"
                                width={150}
                                height={150}
                                style={{ objectFit: 'contain', borderRadius: 4 }}
                                preview={{
                                  src: getStaticImageUrl(answer.image_url)
                                }}
                                onError={(e) => {
                                  console.error('Image load error:', answer.image_url, e);
                                }}
                              />
                              {(selectedCorrect || showCorrect) && (
                                <CheckOutlined style={{ 
                                  color: '#52c41a', 
                                  fontSize: 24,
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  background: '#fff',
                                  borderRadius: '50%',
                                  padding: 4,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }} />
                              )}
                              {selectedWrong && (
                                <CloseOutlined style={{ 
                                  color: '#ff4d4f', 
                                  fontSize: 24,
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  background: '#fff',
                                  borderRadius: '50%',
                                  padding: 4,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }} />
                              )}
                            </div>
                            
                            {/* Радио-кнопка с текстом снизу */}
                            <Radio 
                              value={answer.id} 
                              disabled={answerResults[currentQuestion.id] !== undefined || inputLocked}
                              style={{ 
                                margin: 0
                              }}
                            >
                              <span style={{ 
                                fontSize: '14px', 
                                fontWeight: 400, 
                                color: '#111827'
                              }}>
                                {answer.text}
                              </span>
                            </Radio>
                          </>
                        ) : (
                          <>
                            {/* Без изображения: горизонтальная компоновка - радио-кнопка слева, текст справа */}
                            <Radio 
                              value={answer.id} 
                              disabled={answerResults[currentQuestion.id] !== undefined || inputLocked}
                              style={{ 
                                margin: 0,
                                flexShrink: 0
                              }}
                            >
                              <span style={{ 
                                fontSize: '14px', 
                                fontWeight: 400, 
                                color: '#111827'
                              }}>
                                {answer.text}
                              </span>
                            </Radio>
                            {(selectedCorrect || showCorrect) && (
                              <CheckOutlined style={{ color: '#52c41a', marginLeft: 'auto', fontSize: 18 }} />
                            )}
                            {selectedWrong && (
                              <CloseOutlined style={{ color: '#ff4d4f', marginLeft: 'auto', fontSize: 18 }} />
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                  </div>
                ) : (
                  <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
                    {currentQuestion.answers.map((answer) => {
                      const { selectedCorrect, selectedWrong, showCorrect } =
                        getAnswerVisualState(currentQuestion.id, answer.id, currentAnswer);
                      
                      return (
                        <div
                        key={answer.id}
                        onClick={() => {
                          if (answerResults[currentQuestion.id] === undefined && !inputLocked) {
                            handleAnswerChange(currentQuestion.id, answer.id, 'single_choice');
                          }
                        }}
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 12,
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: selectedCorrect
                              ? '2px solid #52c41a'
                              : selectedWrong
                              ? '2px solid #ff4d4f'
                              : showCorrect
                              ? '2px solid #52c41a'
                              : '1px solid #d9d9d9',
                            backgroundColor: selectedCorrect
                              ? '#f6ffed'
                              : selectedWrong
                              ? '#fff1f0'
                              : showCorrect
                              ? '#f6ffed'
                              : '#ffffff',
                            transition: 'all 0.3s ease',
                            cursor: answerResults[currentQuestion.id] === undefined && !inputLocked ? 'pointer' : 'default',
                            userSelect: 'none',
                            width: '100%',
                            minHeight: '48px',
                            boxSizing: 'border-box'
                          }}
                          onMouseEnter={(e) => {
                            if (answerResults[currentQuestion.id] === undefined && !inputLocked) {
                              e.currentTarget.style.backgroundColor = '#f0f0f0';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (answerResults[currentQuestion.id] === undefined && !inputLocked) {
                              e.currentTarget.style.backgroundColor = selectedCorrect
                                ? '#f6ffed'
                                : selectedWrong
                                ? '#fff1f0'
                                : showCorrect
                                ? '#f6ffed'
                                : '#ffffff';
                            }
                          }}
                        >
                          <Radio 
                            value={answer.id} 
                            disabled={answerResults[currentQuestion.id] !== undefined || inputLocked}
                            style={{ 
                              margin: 0,
                              flexShrink: 0
                            }}
                          >
                            <span style={{ 
                              fontSize: '14px', 
                              fontWeight: 400, 
                              color: '#111827'
                            }}>
                              {answer.text}
                            </span>
                          </Radio>
                          {(selectedCorrect || showCorrect) && (
                            <CheckOutlined style={{ color: '#52c41a', marginLeft: 'auto', fontSize: 18 }} />
                          )}
                          {selectedWrong && (
                            <CloseOutlined style={{ color: '#ff4d4f', marginLeft: 'auto', fontSize: 18 }} />
                          )}
                        </div>
                      );
                    })}
                  </Space>
                )}
              </Radio.Group>
            </div>
          )}

          {currentQuestion.type === 'multiple_choice' && (
            <div style={{ marginTop: 16 }}>
              <Checkbox.Group
                value={currentAnswer?.selected_answer_ids || []}
                onChange={(values) => handleAnswerChange(currentQuestion.id, values, 'multiple_choice')}
                disabled={answerResults[currentQuestion.id] !== undefined || inputLocked}
              >
                {currentQuestion.answers.some(ans => ans.image_url) ? (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'flex-start',
                    gap: 16, 
                    flexWrap: 'wrap',
                    width: '100%',
                    marginTop: 16
                  }}>
                    {currentQuestion.answers.map((answer) => {
                    const { isSelected, selectedCorrect, selectedWrong, showCorrect } =
                      getAnswerVisualState(currentQuestion.id, answer.id, currentAnswer);
                    
                    return (
                      <div
                        key={answer.id}
                        onClick={() => {
                          if (answerResults[currentQuestion.id] === undefined && !inputLocked) {
                            const currentSelected = currentAnswer?.selected_answer_ids || [];
                            const newSelected = currentSelected.includes(answer.id)
                              ? currentSelected.filter((id: number) => id !== answer.id)
                              : [...currentSelected, answer.id];
                            handleAnswerChange(currentQuestion.id, newSelected, 'multiple_choice');
                          }
                        }}
                        style={{
                          display: 'flex',
                          flexDirection: answer.image_url ? 'column' : 'row',
                          alignItems: 'center',
                          justifyContent: answer.image_url ? 'flex-start' : 'flex-start',
                          gap: 12,
                          padding: answer.image_url ? '16px' : '12px 16px',
                          borderRadius: '8px',
                          border: selectedCorrect
                            ? '2px solid #52c41a'
                            : selectedWrong
                            ? '2px solid #ff4d4f'
                            : showCorrect
                            ? '2px solid #52c41a'
                            : '1px solid #d9d9d9',
                          backgroundColor: selectedCorrect
                            ? '#f6ffed'
                            : selectedWrong
                            ? '#fff1f0'
                            : showCorrect
                            ? '#f6ffed'
                            : '#ffffff',
                          transition: 'all 0.3s ease',
                          cursor: answerResults[currentQuestion.id] === undefined && !inputLocked ? 'pointer' : 'default',
                          userSelect: 'none',
                          width: answer.image_url ? '220px' : '100%',
                          minHeight: answer.image_url ? '280px' : '48px',
                          boxSizing: 'border-box'
                        }}
                        onMouseEnter={(e) => {
                          if (answerResults[currentQuestion.id] === undefined && !inputLocked) {
                            e.currentTarget.style.backgroundColor = '#f0f0f0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (answerResults[currentQuestion.id] === undefined && !inputLocked) {
                            e.currentTarget.style.backgroundColor = selectedCorrect
                              ? '#f6ffed'
                              : selectedWrong
                              ? '#fff1f0'
                              : showCorrect
                              ? '#f6ffed'
                              : '#ffffff';
                          }
                        }}
                      >
                        {answer.image_url ? (
                          <>
                            {/* Изображение сверху */}
                            <div style={{ position: 'relative', flexShrink: 0 }}>
                              <Image
                                src={getStaticImageUrl(answer.image_url)}
                                alt="Answer"
                                width={150}
                                height={150}
                                style={{ objectFit: 'contain', borderRadius: 4 }}
                                preview={{
                                  src: getStaticImageUrl(answer.image_url)
                                }}
                                onError={(e) => {
                                  console.error('Image load error:', answer.image_url, e);
                                }}
                              />
                              {(selectedCorrect || showCorrect) && (
                                <CheckOutlined style={{ 
                                  color: '#52c41a', 
                                  fontSize: 24,
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  background: '#fff',
                                  borderRadius: '50%',
                                  padding: 4,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }} />
                              )}
                              {selectedWrong && (
                                <CloseOutlined style={{ 
                                  color: '#ff4d4f', 
                                  fontSize: 24,
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  background: '#fff',
                                  borderRadius: '50%',
                                  padding: 4,
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }} />
                              )}
                            </div>
                            
                            {/* Чекбокс с текстом снизу */}
                            <Checkbox 
                              value={answer.id} 
                              disabled={answerResults[currentQuestion.id] !== undefined || inputLocked}
                              checked={isSelected}
                              style={{ 
                                margin: 0
                              }}
                            >
                              <span style={{ 
                                fontSize: '14px', 
                                fontWeight: 400, 
                                color: '#111827'
                              }}>
                                {answer.text}
                              </span>
                            </Checkbox>
                          </>
                        ) : (
                          <>
                            {/* Без изображения: горизонтальная компоновка - чекбокс слева, текст справа */}
                            <Checkbox 
                              value={answer.id} 
                              disabled={answerResults[currentQuestion.id] !== undefined || inputLocked}
                              checked={isSelected}
                              style={{ 
                                margin: 0,
                                flexShrink: 0
                              }}
                            >
                              <span style={{ 
                                fontSize: '14px', 
                                fontWeight: 400, 
                                color: '#111827'
                              }}>
                                {answer.text}
                              </span>
                            </Checkbox>
                            {(selectedCorrect || showCorrect) && (
                              <CheckOutlined style={{ color: '#52c41a', marginLeft: 'auto', fontSize: 18 }} />
                            )}
                            {selectedWrong && (
                              <CloseOutlined style={{ color: '#ff4d4f', marginLeft: 'auto', fontSize: 18 }} />
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                  </div>
                ) : (
                  <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
                    {currentQuestion.answers.map((answer) => {
                      const { isSelected, selectedCorrect, selectedWrong, showCorrect } =
                        getAnswerVisualState(currentQuestion.id, answer.id, currentAnswer);
                      
                      return (
                        <div
                        key={answer.id}
                        onClick={() => {
                          if (answerResults[currentQuestion.id] === undefined && !inputLocked) {
                            const currentSelected = currentAnswer?.selected_answer_ids || [];
                            const newSelected = currentSelected.includes(answer.id)
                              ? currentSelected.filter((id: number) => id !== answer.id)
                                : [...currentSelected, answer.id];
                              handleAnswerChange(currentQuestion.id, newSelected, 'multiple_choice');
                            }
                          }}
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 12,
                            padding: '12px 16px',
                            borderRadius: '8px',
                            border: selectedCorrect
                              ? '2px solid #52c41a'
                              : selectedWrong
                              ? '2px solid #ff4d4f'
                              : showCorrect
                              ? '2px solid #52c41a'
                              : '1px solid #d9d9d9',
                            backgroundColor: selectedCorrect
                              ? '#f6ffed'
                              : selectedWrong
                              ? '#fff1f0'
                              : showCorrect
                              ? '#f6ffed'
                              : '#ffffff',
                            transition: 'all 0.3s ease',
                            cursor: answerResults[currentQuestion.id] === undefined && !inputLocked ? 'pointer' : 'default',
                            userSelect: 'none',
                            width: '100%',
                            minHeight: '48px',
                            boxSizing: 'border-box'
                          }}
                          onMouseEnter={(e) => {
                            if (answerResults[currentQuestion.id] === undefined && !inputLocked) {
                              e.currentTarget.style.backgroundColor = '#f0f0f0';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (answerResults[currentQuestion.id] === undefined && !inputLocked) {
                              e.currentTarget.style.backgroundColor = selectedCorrect
                                ? '#f6ffed'
                                : selectedWrong
                                ? '#fff1f0'
                                : showCorrect
                                ? '#f6ffed'
                                : '#ffffff';
                            }
                          }}
                        >
                          <Checkbox 
                            value={answer.id} 
                            disabled={answerResults[currentQuestion.id] !== undefined || inputLocked}
                            checked={isSelected}
                            style={{ 
                              margin: 0,
                              flexShrink: 0
                            }}
                          >
                            <span style={{ 
                              fontSize: '14px', 
                              fontWeight: 400, 
                              color: '#111827'
                            }}>
                              {answer.text}
                            </span>
                          </Checkbox>
                          {(selectedCorrect || showCorrect) && (
                            <CheckOutlined style={{ color: '#52c41a', marginLeft: 'auto', fontSize: 18 }} />
                          )}
                          {selectedWrong && (
                            <CloseOutlined style={{ color: '#ff4d4f', marginLeft: 'auto', fontSize: 18 }} />
                          )}
                        </div>
                      );
                    })}
                  </Space>
                )}
              </Checkbox.Group>
            </div>
          )}

          {currentQuestion.type === 'open_answer' && (
            <div>
              <Input.TextArea
                rows={4}
                value={currentAnswer?.answer_text || ''}
                onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value, 'open_answer')}
                placeholder={t('test.enterAnswer')}
                style={{ 
                  marginTop: 16,
                  borderColor: answerResults[currentQuestion.id] !== undefined
                    ? answerResults[currentQuestion.id] ? '#52c41a' : '#ff4d4f'
                    : undefined,
                  borderWidth: answerResults[currentQuestion.id] !== undefined ? 2 : 1
                }}
                disabled={answerResults[currentQuestion.id] !== undefined || inputLocked}
              />
              {answerResults[currentQuestion.id] !== undefined && (
                <div style={{ 
                  marginTop: 8,
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: answerResults[currentQuestion.id] ? '#f6ffed' : '#fff1f0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  {answerResults[currentQuestion.id] ? (
                    <>
                      <CheckOutlined style={{ color: '#52c41a' }} />
                      <span style={{ color: '#52c41a' }}>Правильный ответ</span>
                    </>
                  ) : (
                    <>
                      <CloseOutlined style={{ color: '#ff4d4f' }} />
                      <span style={{ color: '#ff4d4f' }}>Неправильный ответ</span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {currentQuestion.type === 'matching' && (
            <div style={{ marginTop: 16 }}>
              <Paragraph style={{ marginBottom: 24, color: '#666' }}>
                {t('test.matchingInstruction')}
              </Paragraph>
              <Space direction="vertical" style={{ width: '100%', gap: 16 }}>
                {(() => {
                  // Разделяем ответы на левую и правую колонки
                  // Левая колонка: элементы с matching_key (ключи для сопоставления: A, B, C, D)
                  // Правая колонка: элементы БЕЗ matching_key (варианты для выбора)
                  const leftColumn = currentQuestion.answers.filter((ans) => ans.matching_key);
                  const rightColumn = currentQuestion.answers.filter((ans) => !ans.matching_key);
                  
                  // Если правая колонка пустая, возможно все элементы имеют matching_key
                  // В этом случае используем элементы с is_correct=false как левую колонку,
                  // а элементы с is_correct=true как правую колонку
                  let finalLeftColumn = leftColumn;
                  let finalRightColumn = rightColumn;
                  
                  if (rightColumn.length === 0 && leftColumn.length > 0) {
                    // Альтернативная логика: разделяем по is_correct
                    finalLeftColumn = currentQuestion.answers.filter((ans) => ans.matching_key && !ans.is_correct);
                    finalRightColumn = currentQuestion.answers.filter((ans) => ans.matching_key && ans.is_correct);
                  }
                  
                  // Убираем дубликаты из правой колонки по тексту
                  const uniqueRightColumn = finalRightColumn.filter((item, index, self) => 
                    index === self.findIndex((t) => t.text === item.text)
                  );
                  
                  return finalLeftColumn.length > 0 ? finalLeftColumn.map((leftItem) => {
                    const selectedValue = currentAnswer?.matching_data?.[leftItem.matching_key || ''];
                    const isAnswered = answerResults[currentQuestion.id] !== undefined;
                    const correctValue = correctMatching[currentQuestion.id]?.[leftItem.matching_key || ''];
                    
                    return (
                      <div
                        key={leftItem.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 16,
                          padding: '16px',
                          borderRadius: '8px',
                          border: isAnswered && selectedValue
                            ? `2px solid ${selectedValue === correctValue ? '#52c41a' : '#ff4d4f'}`
                            : isAnswered && correctValue
                            ? '2px solid #52c41a'
                            : '1px solid #d9d9d9',
                          backgroundColor: isAnswered && selectedValue
                            ? selectedValue === correctValue ? '#f6ffed' : '#fff1f0'
                            : isAnswered && correctValue
                            ? '#f6ffed'
                            : '#fafafa',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        <div style={{ flex: 1, fontWeight: 500 }}>
                          {leftItem.text}
                        </div>
                        <div style={{ fontSize: 20, color: '#999' }}>→</div>
                        <Select
                          style={{ flex: 1, minWidth: 200 }}
                          placeholder={t('test.selectMatch')}
                          value={selectedValue}
                          onChange={(value) => {
                            const newMatchingData = {
                              ...(currentAnswer?.matching_data || {}),
                              [leftItem.matching_key || '']: value
                            };
                            handleAnswerChange(currentQuestion.id, newMatchingData, 'matching');
                          }}
                          disabled={isAnswered || inputLocked}
                          notFoundContent={uniqueRightColumn.length === 0 ? t('test.noOptions') : null}
                        >
                          {uniqueRightColumn.map((rightItem) => (
                            <Select.Option key={rightItem.id} value={rightItem.text}>
                              {rightItem.text}
                            </Select.Option>
                          ))}
                        </Select>
                        {isAnswered && (
                          <div style={{ marginLeft: 8 }}>
                            {selectedValue === correctValue ? (
                              <CheckOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                            ) : selectedValue ? (
                              <CloseOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
                            ) : correctValue ? (
                              <CheckOutlined style={{ color: '#52c41a', fontSize: 20, opacity: 0.5 }} />
                            ) : null}
                          </div>
                        )}
                      </div>
                    );
                  }) : (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                      {t('test.matchingError')}
                    </div>
                  );
                })()}
              </Space>
            </div>
          )}

          {/* Пояснение после неверного ответа (микроблог) */}
          {answerResults[currentQuestion.id] === false &&
            (currentQuestion.explanation || (currentQuestion.explanation_image_urls?.length ?? 0) > 0) && (
            <Card
              size="small"
              style={{
                marginTop: 24,
                background: '#fafafa',
                border: '1px solid #e8e8e8',
                borderRadius: '12px',
              }}
              bodyStyle={{ padding: '16px 20px' }}
            >
              <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: 8, fontWeight: 600 }}>
                Пояснение
              </div>
              {currentQuestion.explanation && (
                <Paragraph style={{ marginBottom: currentQuestion.explanation_image_urls?.length ? 16 : 0, whiteSpace: 'pre-wrap' }}>
                  {currentQuestion.explanation}
                </Paragraph>
              )}
              {currentQuestion.explanation_image_urls?.map((url, idx) => {
                const caption = getExplanationImageCaption(currentQuestion.explanation_image_captions?.[idx], url, idx);
                return (
                  <div key={idx} style={{ marginBottom: idx < (currentQuestion.explanation_image_urls?.length ?? 0) - 1 ? 12 : 0 }}>
                    <Image
                      src={getStaticImageUrl(url)}
                      alt={caption}
                      style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 8 }}
                      preview={{ mask: 'Увеличить' }}
                    />
                    <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: '#262626' }}>
                      {caption}
                    </div>
                  </div>
                );
              })}
            </Card>
          )}
        </Card>

        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={handlePrevious} disabled={currentQuestionIndex === 0 || inputLocked}>
            {t('test.back')}
          </Button>
          <Button
            type="primary"
            onClick={handleNext}
            loading={submitting}
            disabled={inputLocked || (answerResults[currentQuestion.id] === undefined && (
              (currentQuestion.type === 'single_choice' && !currentAnswer?.selected_answer_ids?.length) ||
              (currentQuestion.type === 'multiple_choice' && !currentAnswer?.selected_answer_ids?.length) ||
              (currentQuestion.type === 'open_answer' && !currentAnswer?.answer_text?.trim()) ||
              (currentQuestion.type === 'image_identification' && (() => {
                const parts = (currentAnswer?.answer_text || '').split('|');
                return parts.length < 2 || parts.some((part) => !part.trim());
              })()) ||
              (currentQuestion.type === 'matching' && currentQuestion.answers.filter((answer) => answer.matching_key)
                .some((answer) => !currentAnswer?.matching_data?.[answer.matching_key!]?.trim()))
            ))}
          >
            {answerResults[currentQuestion.id] === undefined
              ? t('test.checkAnswer')
              : currentQuestionIndex === testData.questions.length - 1
                ? t('test.complete')
                : t('test.next')}
          </Button>
        </div>
      </Card>
    </Layout>
  );
};

export default TestPage;
